import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useBadgeDefinitions } from '@/hooks/useBadgeDefinitions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Award, X, Plus, UserCheck } from 'lucide-react';

interface AwardBadgeFormProps {
  onSuccess?: () => void;
  preselectedBadgeD?: string;
}

function resolveToHex(input: string): string | null {
  const trimmed = input.trim();
  try {
    if (trimmed.startsWith('npub1')) {
      const decoded = nip19.decode(trimmed);
      if (decoded.type === 'npub') return decoded.data as string;
    }
    if (trimmed.startsWith('nprofile1')) {
      const decoded = nip19.decode(trimmed);
      if (decoded.type === 'nprofile') return (decoded.data as { pubkey: string }).pubkey;
    }
    // Raw hex
    if (/^[0-9a-f]{64}$/.test(trimmed)) return trimmed;
  } catch {
    // fall through
  }
  return null;
}

export function AwardBadgeForm({ onSuccess, preselectedBadgeD }: AwardBadgeFormProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: badges = [] } = useBadgeDefinitions(user?.pubkey);

  const [selectedD, setSelectedD] = useState(preselectedBadgeD ?? '');
  const [npubInput, setNpubInput] = useState('');
  const [awardees, setAwardees] = useState<string[]>([]);

  const addAwardee = () => {
    const hex = resolveToHex(npubInput);
    if (!hex) {
      toast({ title: 'Invalid npub or pubkey', variant: 'destructive' });
      return;
    }
    if (awardees.includes(hex)) {
      toast({ title: 'Already in list', variant: 'destructive' });
      return;
    }
    setAwardees((prev) => [...prev, hex]);
    setNpubInput('');
  };

  const removeAwardee = (hex: string) => {
    setAwardees((prev) => prev.filter((p) => p !== hex));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!selectedD) {
      toast({ title: 'Select a badge', variant: 'destructive' });
      return;
    }
    if (awardees.length === 0) {
      toast({ title: 'Add at least one recipient', variant: 'destructive' });
      return;
    }

    const badgeRef = `30009:${user.pubkey}:${selectedD}`;
    const tags: string[][] = [
      ['a', badgeRef],
      ...awardees.map((p) => ['p', p]),
    ];

    try {
      await publish({ kind: 8, content: '', tags });

      const selectedBadge = badges.find((b) => b.d === selectedD);
      toast({
        title: 'Badge awarded!',
        description: `"${selectedBadge?.name ?? selectedD}" sent to ${awardees.length} recipient(s).`,
      });

      setSelectedD('');
      setAwardees([]);

      await queryClient.invalidateQueries({ queryKey: ['nostr', 'badge-awards-issued', user.pubkey] });
      onSuccess?.();
    } catch (err) {
      toast({
        title: 'Failed to award badge',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!user) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 text-center">
          <p className="text-muted-foreground">Please log in to award badges.</p>
        </CardContent>
      </Card>
    );
  }

  const selectedBadge = badges.find((b) => b.d === selectedD);

  return (
    <Card className="border-border/60 bg-card/80 backdrop-blur">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Award Badge</CardTitle>
        </div>
        <CardDescription>
          Issue a NIP-58 badge award (kind 8) to one or more Nostr users.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Badge selector */}
          <div className="space-y-1.5">
            <Label>Select Badge</Label>
            {badges.length === 0 ? (
              <p className="text-sm text-muted-foreground rounded-md border border-dashed border-border p-3">
                No badges yet. Create a badge first.
              </p>
            ) : (
              <Select value={selectedD} onValueChange={setSelectedD}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a badge to award…" />
                </SelectTrigger>
                <SelectContent>
                  {badges.map((badge) => (
                    <SelectItem key={badge.d} value={badge.d}>
                      <div className="flex items-center gap-2">
                        {badge.thumbs[0]?.url || badge.image ? (
                          <img
                            src={badge.thumbs[0]?.url ?? badge.image}
                            alt=""
                            className="h-5 w-5 rounded object-cover"
                          />
                        ) : (
                          <div className="h-5 w-5 rounded bg-primary/20 flex items-center justify-center">
                            <Award className="h-3 w-3 text-primary" />
                          </div>
                        )}
                        <span>{badge.name ?? badge.d}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {selectedBadge && (
            <div className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 p-3">
              {(selectedBadge.thumbs[0]?.url || selectedBadge.image) ? (
                <img
                  src={selectedBadge.thumbs[0]?.url ?? selectedBadge.image}
                  alt={selectedBadge.name}
                  className="h-12 w-12 rounded-lg object-cover border border-border/40"
                />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center border border-border/40">
                  <Award className="h-6 w-6 text-primary" />
                </div>
              )}
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{selectedBadge.name ?? selectedBadge.d}</p>
                {selectedBadge.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{selectedBadge.description}</p>
                )}
              </div>
            </div>
          )}

          {/* Awardee input */}
          <div className="space-y-1.5">
            <Label htmlFor="award-npub" className="flex items-center gap-1.5">
              <UserCheck className="h-3.5 w-3.5" /> Recipients
            </Label>
            <div className="flex gap-2">
              <Input
                id="award-npub"
                placeholder="npub1… or hex pubkey"
                value={npubInput}
                onChange={(e) => setNpubInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAwardee();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={addAwardee}
                className="shrink-0"
                aria-label="Add recipient"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Press Enter or click + to add multiple recipients.
            </p>
          </div>

          {/* Awardee list */}
          {awardees.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                {awardees.length} recipient(s) queued
              </Label>
              <div className="flex flex-wrap gap-2 max-h-36 overflow-y-auto rounded-lg border border-border/40 bg-muted/20 p-2">
                {awardees.map((hex) => {
                  let display: string;
                  try {
                    display = nip19.npubEncode(hex);
                  } catch {
                    display = hex;
                  }
                  return (
                    <Badge
                      key={hex}
                      variant="secondary"
                      className="flex items-center gap-1 font-mono text-[10px] max-w-[180px]"
                    >
                      <span className="truncate">{display.slice(0, 16)}…</span>
                      <button
                        type="button"
                        onClick={() => removeAwardee(hex)}
                        aria-label={`Remove ${display}`}
                        className="ml-0.5 rounded hover:bg-destructive/20 focus-visible:outline-none"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isPending || badges.length === 0 || awardees.length === 0}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? 'Awarding…' : `Award to ${awardees.length || '…'} recipient(s)`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
